import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Header } from "./components/layout/Header"
import { Hero } from "./components/sections/Hero"
import { Partners } from "./components/sections/Partners"
import { Process } from "./components/sections/Process"
import { Projects } from "./components/sections/Projects"
import { Services } from "./components/sections/Services"
import { Contact } from "./components/sections/Contact"
import { Toaster } from "@/components/ui/toaster"
import { Footer } from "./components/layout/Footer"
import { useTranslation } from "react-i18next"
import { StickyCTA } from "./components/common/StickyCTA"
import { WhatsAppButton } from "./components/common/WhatsAppButton"
import { SEO } from "./components/seo/SEO"
import { Preloader } from "./components/common/Preloader";
import { AnimatePresence } from "framer-motion";
import { ServiceDetailModal } from './components/common/ServiceDetailModal';
import { CaseStudyDetailModal } from "./components/common/CaseStudyDetailModal";
import { CookieConsent } from "./components/common/CookieConsent";
import { Analytics } from "@/components/seo/Analytics";
import { SkipLink } from "@/components/common/SkipLink";
import { SectionQuickNav } from "./components/common/SectionQuickNav";
import { Link } from "react-router-dom";
import { MissionPillars } from "./components/sections/MissionPillars";

function App() {
  const { t, i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(() => {
    if (typeof window === 'undefined') return true;
    return !window.sessionStorage.getItem('ins-site-loaded');
  });
  const location = useLocation();

  useEffect(() => {
    document.documentElement.lang = i18n.language;
    document.documentElement.dir = i18n.dir(i18n.language);
  }, [i18n, i18n.language]);

  // Scroll to section when arriving on home with a hash (e.g., /#services)
  useEffect(() => {
    if (location.pathname !== '/' || !location.hash || isLoading) {
      return;
    }

    const id = location.hash.slice(1);
    const scrollToSection = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return true;
      }
      return false;
    };

    let timeoutId: ReturnType<typeof window.setTimeout> | undefined;
    const rafId = window.requestAnimationFrame(() => {
      if (!scrollToSection()) {
        timeoutId = window.setTimeout(() => {
          scrollToSection();
        }, 200);
      }
    });

    return () => {
      window.cancelAnimationFrame(rafId);
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [location.pathname, location.hash, isLoading]);

  
  useEffect(() => {
    if (!isLoading) {
      document.body.style.overflow = 'auto';
      return;
    }

    const onLoad = () => {
      setIsLoading(false);
      window.sessionStorage.setItem('ins-site-loaded', 'true');
      document.body.style.overflow = 'auto';
    };

    const timer = setTimeout(onLoad, 800);
    window.addEventListener('load', onLoad);

    document.body.style.overflow = 'hidden';

    return () => {
      clearTimeout(timer);
      window.removeEventListener('load', onLoad);
      document.body.style.overflow = 'auto';
    }
  }, [isLoading]);

  return (
    <>
      <AnimatePresence>
        {isLoading && <Preloader />}
      </AnimatePresence>

      {!isLoading && (
         <div className="bg-neutral-50 min-h-screen text-neutral-900 flex flex-col">
            <SkipLink />
            <SEO />
            <Analytics />
            <Header />
            <SectionQuickNav />
            <main id="main-content" className="flex-grow">
              <Hero />
              <Partners />
              <Services />
              <MissionPillars />
              <Process />
              <Projects />
              <Contact />
              <div className="bg-neutral-50 py-20">
                <div className="container mx-auto px-4 text-center">
                  <h2 className="text-3xl font-bold text-neutral-900">{t("blog.title", "Field Notes & Updates")}</h2>
                  <p className="mt-3 text-neutral-600">{t("blog.homeCta", "Read fresh stories from our Medium publication.")}</p>
                  <Link
                    to="/blog"
                    className="mt-6 inline-flex items-center gap-2 rounded-full border border-brand-turquoise bg-brand-turquoise px-6 py-3 text-sm font-semibold text-white transition hover:bg-teal-700"
                  >
                    {t("blog.cta", "Visit Blog")}
                  </Link>
                </div>
              </div>
            </main>
            <Footer />
            <ServiceDetailModal />
            <CaseStudyDetailModal />
            <CookieConsent />
            <Toaster />
            <StickyCTA />
            <WhatsAppButton />
          </div>
      )}
    </>
  )
}

export default App
